import { ArrowRight, Check, Clock3, List } from 'lucide-react';
import { useRef } from 'react';

import { formatMinutes } from '@/shared/ui/activity/activity-kind';
import { outlineOf } from '@/shared/ui/activity/outline';
import { ReadingDocument } from '@/shared/ui/activity/reading-document';
import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

export function WorkshopInstructions({
  title,
  body,
  acceptance,
  summary,
  estimatedMinutes,
  onReview,
  progressScope,
}: {
  title: string;
  body: string;
  acceptance: string[];
  summary?: string;
  estimatedMinutes?: number | null;
  onReview: () => void;
  /** Identifies this workshop for the learner's own Done-when ticks. */
  progressScope?: string;
}) {
  const reader = useRef<HTMLDivElement>(null);
  const jumpTarget = useRef<HTMLElement | null>(null);
  const sectioned = body.startsWith('```reading-sections');
  const outline = sectioned ? [] : outlineOf(body);
  const minutes = formatMinutes(estimatedMinutes);
  function jump(id: string) {
    const root = reader.current;
    const target = Array.from(root?.querySelectorAll<HTMLElement>('[id]') ?? []).find(
      (element) => element.id === id,
    );
    if (!root) return;
    root.scrollTo({
      top: target
        ? target.getBoundingClientRect().top -
          root.getBoundingClientRect().top +
          root.scrollTop -
          24
        : 0,
      behavior: 'instant',
    });
    if (target) {
      target.tabIndex = -1;
      jumpTarget.current = target;
    }
  }
  function submissionFooter() {
    return (
      <>
        {acceptance.length ? (
          <section
            className="border-line bg-surface/40 mt-8 rounded-lg border p-4"
            aria-labelledby="workshop-acceptance"
          >
            <h2 id="workshop-acceptance" className="text-fg text-sm font-semibold">
              Completion criteria
            </h2>
            <ul className="mt-3 space-y-3 text-[13px] leading-6">
              {acceptance.map((criterion) => (
                <li key={criterion} className="flex gap-2.5">
                  <Check className="text-fg-muted mt-1 size-3.5 shrink-0" aria-hidden="true" />
                  <span>{criterion}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="border-line mt-8 border-t pt-5">
          <p className="text-fg-muted mb-3 text-xs leading-5">
            After trying the milestone experiments, explain your approach and submit the complete
            application from the Submission tab.
          </p>
          <Button variant="secondary" size="sm" onClick={onReview}>
            Prepare submission <ArrowRight className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="border-line flex h-11 shrink-0 items-center gap-2 border-b px-5">
        <List className="text-fg-muted size-3.5 shrink-0" aria-hidden="true" />
        {sectioned ? (
          <span className="text-fg-muted flex-1 text-xs">Build milestones</span>
        ) : (
          <SelectField
            aria-label="Jump to instruction section"
            value=""
            onValueChange={jump}
            finalFocus={() => {
              const target = jumpTarget.current;
              jumpTarget.current = null;
              return target ?? true;
            }}
            placeholder="Jump to section"
            size="sm"
            className="min-w-0 flex-1"
            options={[
              { value: 'workshop-overview', label: 'Overview' },
              ...outline.map((entry) => ({ value: entry.id, label: entry.title })),
              ...(acceptance.length
                ? [{ value: 'workshop-acceptance', label: 'Completion criteria' }]
                : []),
            ]}
          />
        )}
        {minutes ? (
          <span className="text-fg-muted inline-flex shrink-0 items-center gap-1.5 text-[12px]">
            <Clock3 className="size-3" aria-hidden="true" />
            {minutes}
          </span>
        ) : null}
      </div>
      <div
        ref={reader}
        data-scroll-pane="workshop-instructions"
        className="workshop-instructions min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-6"
      >
        <header className="mb-6">
          <h1
            id="workshop-overview"
            className="text-fg text-[22px] font-semibold leading-tight tracking-[-0.025em]"
          >
            {title}
          </h1>
          {summary ? <p className="text-fg-muted mt-3 text-[13px] leading-6">{summary}</p> : null}
        </header>
        <ReadingDocument
          source={body}
          workshopScope={progressScope ?? title}
          renderSectionFooter={(_section, index, total) =>
            index === total - 1 ? submissionFooter() : null
          }
          renderDocumentFooter={submissionFooter}
        />
      </div>
    </>
  );
}
